import React, { useMemo } from 'react';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { getEffectiveDealRate } from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';
import { getDealCardCopy } from '@/standard-unit-listing/deals';
import { UnitTypeWithImages } from '@/standard-unit-listing/screens';

import classes from './styles.module.scss';

import PriceInfo from './PriceInfo';

export interface DealPriceCardsProps {
    unitType: UnitTypeWithImages;
    location: Location;
    /** Currently selected deal id, or null when the standard price is selected. */
    selectedDealId: string | null;
    onSelectDeal: (dealId: string | null) => void;
}

/**
 * Renders the stack of selectable "Price Info" cards for a unit type: one card
 * per available deal, highlighting the active selection and showing the standard
 * rate struck-through on discounted deals. When the unit has no deals, the
 * standard rate is shown on its own without the card border. Cards are only
 * interactive when there is more than one deal to choose between.
 */
export default function DealPriceCards({
    unitType,
    location,
    selectedDealId,
    onSelectDeal,
}: DealPriceCardsProps) {
    const deals = unitType.deals ?? [];
    const hasDeals = deals.length > 0;
    const standardRate = unitType.rates.rate;

    // Billing period suffix (e.g. "/mo") derived from the shared price label
    // formatter so it stays consistent with weekly / 4-weekly billing configs.
    const periodSuffix = useMemo(() => {
        const symbol = location.rapidstor.currencySymbol ?? '';
        return formatPriceLabel('', location, false, false).slice(symbol.length);
    }, [location]);

    const selectable = deals.length > 1;

    return (
        <div className={classes.priceInfoStack}>
            {deals.map((deal) => {
                const effectiveRate = getEffectiveDealRate(deal, standardRate);
                const hasSaving = effectiveRate < standardRate;
                const copy = getDealCardCopy(deal, location);

                return (
                    <PriceInfo
                        key={deal._id}
                        discountLabel={copy.label}
                        primaryLine={copy.primary}
                        secondaryLine={copy.secondary}
                        price={formatPriceLabel(
                            formatCurrency(effectiveRate, location),
                            location,
                            false,
                            true,
                        )}
                        period={periodSuffix}
                        originalPrice={hasSaving
                            ? formatPriceLabel(
                                formatCurrency(standardRate, location),
                                location,
                                false,
                                false,
                            )
                            : undefined}
                        active={selectedDealId === deal._id}
                        selectable={selectable}
                        onSelect={() => onSelectDeal(deal._id)}
                    />
                );
            })}

            {!hasDeals ? (
                <PriceInfo
                    borderless
                    price={formatPriceLabel(
                        formatCurrency(standardRate, location),
                        location,
                        false,
                        true,
                    )}
                    period={periodSuffix}
                />
            ) : null}
        </div>
    );
}
